/**
 * Print-area / cutout drawing tools — the manual fallback, plus the geometry status.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-12). The print area is normally auto-detected from the
 * magenta marker; this row is the manual path. `adjustCornerRadius`'s continuous 0..100% controller
 * and the neutral "Draw or detect the print area to begin" empty state are unchanged.
 * 
 * `RADIUS_STEP` moved with the +/- buttons: they are its only reader.
 */
import type { Mask, Polygon, ValidationResult } from "@genz/engine-bind";
import { Icon } from "../../ui/Icon.js";
import { InfoHint } from "../../ui/Tooltip.js";
import { MAX_CORNER_RADIUS } from "../corners.js";

/** Corner-radius step per +/- press (normalized). */
const RADIUS_STEP = 0.1;

export interface CutoutToolsCardProps {
  locked?: boolean;
  printArea: Polygon;
  cutouts: Mask[];
  active: Mask | undefined;
  activeRadius: number;
  activeCutout: number;
  validation: ValidationResult;
  addCutout: () => void;
  addShapeCutout: (kind: "ellipse" | "rounded") => void;
  deleteActiveCutout: () => void;
  seedPrintArea: () => void;
  smoothActiveCutout: () => void;
  adjustCornerRadius: (delta: number, set?: number) => void;
}

export function CutoutToolsCard(p: CutoutToolsCardProps) {
  const {
    locked, printArea, cutouts, active, activeRadius,
    activeCutout, validation, addCutout, addShapeCutout, deleteActiveCutout,
    seedPrintArea, smoothActiveCutout, adjustCornerRadius,
  } = p;
  return (
    <>
      {/* Print-area / cutout drawing tools — a manual fallback (the print area is
          auto-detected from the magenta marker). Inline row of small actions; the last
          card in the right-hand control column, so it never overlaps the canvas. */}
      <div className="mask-editor__toolbar" data-testid="cutout-tools">
        {printArea.length < 3 && !locked && (
          <button
            type="button"
            className="btn btn--sm"
            onClick={seedPrintArea}
            data-testid="seed-print-area"
            title="Seed a default print-area rectangle to trace"
          >
            <Icon name="image" size={16} /> Seed rectangle
          </button>
        )}
        <button type="button" className="btn btn--sm" onClick={addCutout} disabled={locked} data-testid="cutout-add">
          + Cutout
        </button>
        <button type="button" className="btn btn--sm" onClick={() => addShapeCutout("ellipse")} disabled={locked} data-testid="cutout-add-ellipse" title="Add an ellipse cutout (camera lens)">
          + Ellipse
        </button>
        <button type="button" className="btn btn--sm" onClick={() => addShapeCutout("rounded")} disabled={locked} data-testid="cutout-add-rounded" title="Add a rounded-rectangle cutout">
          + Rounded
        </button>
        {active && (
          <span className="mask-editor__radius-label" data-testid="cutout-active-label">
            Cutout {activeCutout + 1}/{cutouts.length}
          </span>
        )}
        {active && (
          <button type="button" className="btn btn--sm" onClick={smoothActiveCutout} disabled={locked} data-testid="cutout-smooth" title="Smooth the active cutout into a curve">
            ∿ Smooth
          </button>
        )}
        {active && (
          <button
            type="button"
            className="btn btn--sm btn--danger"
            onClick={deleteActiveCutout}
            disabled={locked}
            data-testid="cutout-delete"
            title="Delete the entire active cutout"
          >
            <Icon name="trash" size={16} /> Delete cutout
          </button>
        )}
        {active && (
          <span className="mask-editor__radius" data-testid="corner-radius-controls">
            <span className="mask-editor__radius-label">
              Cutout corners {Math.round((activeRadius / MAX_CORNER_RADIUS) * 100)}%
              {activeRadius >= MAX_CORNER_RADIUS ? " · circle" : ""}
            </span>
            {/* Continuous corner-rounding controller: drag 0 (square) → 100% (full
                circle/pill). Maps the slider 0..100 onto the normalized radius
                0..MAX_CORNER_RADIUS and regenerates the rounded-rect polygon live. */}
            <input
              type="range"
              className="mask-editor__radius-slider"
              min={0}
              max={100}
              step={1}
              value={Math.round((activeRadius / MAX_CORNER_RADIUS) * 100)}
              onChange={(e) => adjustCornerRadius(0, (Number(e.target.value) / 100) * MAX_CORNER_RADIUS)}
              disabled={locked}
              aria-label="Cutout corner radius (0% = square, 100% = circle)"
              data-testid="radius-slider"
            />
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => adjustCornerRadius(-RADIUS_STEP)}
              disabled={locked || activeRadius <= 0}
              data-testid="radius-dec"
            >
              − radius
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => adjustCornerRadius(RADIUS_STEP)}
              disabled={locked || activeRadius >= MAX_CORNER_RADIUS}
              data-testid="radius-inc"
            >
              + radius
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => adjustCornerRadius(0, MAX_CORNER_RADIUS)}
              disabled={locked || activeRadius >= MAX_CORNER_RADIUS}
              data-testid="radius-circle"
            >
              Circle
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => adjustCornerRadius(0, 0)}
              disabled={locked || activeRadius <= 0}
              data-testid="radius-off"
            >
              Off
            </button>
          </span>
        )}
        {/* WS-Z6 — rule 10 + MI-4. This was a `⌨` glyph standing in for an icon on a chip
            whose entire content rode a `title`: a third explanation channel AND a picture
            drawn with a character. `InfoHint` is the one channel, and its ⓘ is a real Icon. */}
        <span className="mask-editor__hint mask-editor__shortcuts">
          Shortcuts
          <InfoHint
            label="Canvas shortcuts"
            text="Alt-click: add print-area point · Shift-click: add cutout point · Double-click: delete · Click a print-area point then arrow keys nudge it (Shift = 10px)"
          />
        </span>
        {printArea.length < 3 ? (
          // Nothing drawn yet → a neutral prompt, NOT an alarming "Invalid"
          // (cutouts are optional; an empty editor isn't an error).
          <span className="mask-editor__neutral" data-testid="validation-status">
            Draw or detect the print area to begin.
          </span>
        ) : (
          <span
            className={validation.valid ? "mask-editor__valid" : "mask-editor__invalid"}
            data-testid="validation-status"
          >
            {validation.valid ? "Geometry valid" : `Invalid: ${validation.errors.join(", ")}`}
          </span>
        )}
      </div>
    </>
  );
}
